import React, { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Hls from "hls.js";
import { getMovieDetail } from "../api/movieApi";
import Loading from "../components/Loading/Loading";
import "./../assets/styles/css/VideoPlayerPage.css";
import { CiBoxList } from "react-icons/ci";
import { GrFormNext, GrFormPrevious   } from "react-icons/gr";
const VideoPlayerPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [videoUrl, setVideoUrl] = useState("");
  const [error, setError] = useState(null);
  const [videoTitle, setVideoTitle] = useState("");
  const [videoId, setVideoId] = useState(null);
  const [episodes, setEpisodes] = useState([]);
  const [currentEpisodeIndex, setCurrentEpisodeIndex] = useState(0);
  const [isPopupVisible, setIsPopupVisible] = useState(false);
  const [loading, setLoading] = useState(true);

  const videoRef = useRef(null);
  const [borderRadius, setBorderRadius] = useState(window.innerWidth >= 1000 ? "8px" : "0px");
  
  const MAX_SAVE_DAYS = 1;

  const clearOldProgress = () => {
    const now = new Date().getTime();
    for (const key in localStorage) {
      if (key.startsWith("videoTime_")) {
        const data = JSON.parse(localStorage.getItem(key));
        if (data && data.timestamp) {
          const savedTime = data.timestamp;
          if (now - savedTime > MAX_SAVE_DAYS * 24 * 60 * 60 * 1000) {
            localStorage.removeItem(key);
            console.log(`Đã xóa tiến trình cũ: ${key}`);
          }
        }
      }
    }
  };

  const extractSlugs = (pathname) => {
    const regex = /^\/phim\/([^/]+)\/([^/]+)$/;
    const match = pathname.match(regex);
    if (match) {
      return { movieSlug: match[1], episodeSlug: match[2] };
    }
    return { movieSlug: undefined, episodeSlug: undefined };
  };

  const handleEpisodeChange = (newIndex) => {
    if (newIndex >= 0 && newIndex < episodes.length) {
      const newEpisode = episodes[newIndex];
      setCurrentEpisodeIndex(newIndex);
      navigate(`/phim/${newEpisode.movieSlug}/${newEpisode.slug}`, {
        state: { videoUrl: newEpisode.link_m3u8, TitleName: newEpisode.filename },
      });
    }
  };

  useEffect(() => {
    const video = document.getElementById("video-player");
    if (!video) return;
  
    let keyPresses = [];
    let timeout;
  
    const handleKeyDown = (event) => {
      if (event.key === "ArrowRight" || event.key === "ArrowLeft") {
        event.preventDefault();
        const now = Date.now();
  
        keyPresses = keyPresses.filter((time) => now - time < 1500);
        keyPresses.push(now);
  
        if (keyPresses.length >= 5) {
          video.currentTime += event.key === "ArrowRight" ? 60 : -60;
          keyPresses = []; 
        } else {
          video.currentTime += event.key === "ArrowRight" ? 15 : -15;
        }
      }
    };
  
    document.addEventListener("keydown", handleKeyDown);
    
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      clearTimeout(timeout);
    };
  }, []);  

  const togglePopup = () => {
    setIsPopupVisible(!isPopupVisible);
  };

  useEffect(() => {
    const handleResize = () => {
      setBorderRadius(window.innerWidth <= 1000 ? "8px" : "0px");
    };

    const handleFullscreenChange = () => {
      const video = document.getElementById("video-player");
      if (!video) return;

      if (document.fullscreenElement === video) {
        video.style.borderRadius = "0px"; 
      } else {
        video.style.borderRadius = borderRadius; 
      }
    };

    window.addEventListener("resize", handleResize);
    document.addEventListener("fullscreenchange", handleFullscreenChange);

    return () => {
      window.removeEventListener("resize", handleResize);
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
  }, [borderRadius]);

  const renderEpisodeList = () => {
    return (
      <div className="popup">
        <div className="popup-content">
          <ul>
            {episodes.map((episode, index) => (
              <li key={episode.slug}>
                <button
                  onClick={() => {
                    handleEpisodeChange(index);
                    setIsPopupVisible(false);
                  }}
                  disabled={currentEpisodeIndex === index}
                >
                  {episode.name || `Tập ${index + 1}`}
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>
    );
  };
  
  useEffect(() => {
    if (isPopupVisible) {
      const handleClickOutside = (event) => {
        const popupContent = document.querySelector(".popup-content");
        if (popupContent && !popupContent.contains(event.target)) {
          setIsPopupVisible(false);
        }
      };
  
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [isPopupVisible]);  

  useEffect(() => {
    clearOldProgress();

    const fetchData = async () => {
      try {
        const { movieSlug, episodeSlug } = extractSlugs(location.pathname);
        if (!movieSlug || !episodeSlug) {
          setError("URL không hợp lệ.");
          return;
        }
        const response = await getMovieDetail(movieSlug);
        if (response.data?.episodes) {
          const allEpisodes = response.data.episodes.flatMap((server) =>
            server.server_data.map((ep) => ({
              ...ep,
              movieSlug,
            }))
          );
          setEpisodes(allEpisodes);

          const currentEpisodeIndex = allEpisodes.findIndex(
            (ep) => ep.slug === episodeSlug
          );
          setCurrentEpisodeIndex(currentEpisodeIndex);

          const currentEpisode = allEpisodes[currentEpisodeIndex];
          if (currentEpisode) {
            setVideoUrl(currentEpisode.link_m3u8);
            setVideoId(`${movieSlug}_${episodeSlug}`);
            const regex = /(.+)\s-\sTập\s(\d+)/;
            const match = currentEpisode.filename.match(regex);
            if (match) {
              setVideoTitle(`${match[1]} - Tập ${match[2]}`);
            } else {
              setVideoTitle(currentEpisode.filename || "Tập phim không có tiêu đề");
            }
          } else {
            setError("Không tìm thấy tập phim này.");
          }
        } else {
          setError("Không tìm thấy thông tin phim.");
        }
      } catch (err) {
        console.error("Lỗi khi gọi API:", err);
        setError("Lỗi trong quá trình tải dữ liệu.");
      }finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [location]);

  useEffect(() => {
    const video = document.getElementById("video-player");
    if (!video) {
      return;
    }
      
    if (video.canPlayType('application/vnd.apple.mpegurl')) {
      console.log("Sử dụng HLS native.");
      video.src = videoUrl;
      video.addEventListener('loadedmetadata', () => {
        const savedData = localStorage.getItem(`videoTime_${videoId}`);
        if (savedData) {
          const parsedData = JSON.parse(savedData);
          if (parsedData && parsedData.time) {
            video.currentTime = parseFloat(parsedData.time);
          }
        }
      });
    } else if (Hls.isSupported()) {
      console.log("Sử dụng Hls.js.");
      const hls = new Hls();
      hls.loadSource(videoUrl);
      hls.attachMedia(video);
  
      hls.on(Hls.Events.MANIFEST_PARSED, function () {
        console.log("Manifest loaded, starting the stream");
      });
  
      hls.on(Hls.Events.ERROR, function (event, data) {
        console.error("HLS Error:", data);
        if (data.fatal) {
          switch (data.type) {
            case Hls.ErrorTypes.NETWORK_ERROR:
              setError("Network error occurred while loading the video.");
              hls.destroy();
              break;
            case Hls.ErrorTypes.MEDIA_ERROR:
              setError("Media error occurred while loading the video.");
              hls.destroy();
              break;
            default:
              setError("An unknown error occurred.");
              hls.destroy();
              break;
          }
        }
      });
  
      video.ontimeupdate = () => {
        localStorage.setItem(
          `videoTime_${videoId}`,
          JSON.stringify({
            time: video.currentTime,
            timestamp: new Date().getTime(),
          })
        );
      };
  
      const savedData = localStorage.getItem(`videoTime_${videoId}`);
      if (savedData) {
        const parsedData = JSON.parse(savedData);
        if (parsedData && parsedData.time) {
          video.currentTime = parseFloat(parsedData.time);
        }
      }
  
      return () => {
        hls.destroy();
      };
    } else {
      setError("Trình duyệt của bạn không hỗ trợ HLS.");
    }
  }, [videoUrl, videoId]);

  if (error) {
    return <p className="error">{error}</p>;
  }
  if (loading) return <Loading />;
  return (
    <div className="video-player-page">
      <video id="video-player" width="60%" controls ref={videoRef} style={{ borderRadius }} >
        Trình duyệt của bạn không hỗ trợ thẻ video.
      </video>
      <div className="video-title" style={{ marginBottom: "20px" }}>
        {videoTitle ? videoTitle : ""}
      </div>
      <div className="episode-controls">
        <button
          onClick={() => handleEpisodeChange(currentEpisodeIndex - 1)}
          style={{ display: currentEpisodeIndex === 0 ? 'none' : 'flex' }}
        >
          <GrFormPrevious/> Tập trước 
        </button>
        <button onClick={togglePopup}>
          <CiBoxList /> 
      </button>
        <button
          onClick={() => handleEpisodeChange(currentEpisodeIndex + 1)}
          style={{ display: currentEpisodeIndex === episodes.length - 1 ? 'none' : 'flex' }}
        >
          Tập sau <GrFormNext/>
        </button>
      </div>
      {isPopupVisible && renderEpisodeList()}
    </div>
  );
};

export default VideoPlayerPage;
